import type { components } from '@connected/api-client/api.generated';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { usePatcher } from '@connected/api-client/hooks/use-patcher';
import { debounceTime } from '@connected/api-client/patcher';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { CheckboxField } from '@connected/form-fields/components/checkbox';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import {
    Button,
} from '@connected/ui/components/button';
import {
    Input,
} from '@connected/ui/components/input';
import {

    Separator,
} from '@connected/ui/components/separator';
import { Section } from '@connected/ui/features/layouts';
import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ContactPersonsList } from '@/components/contact-persons';
import { InformationLine } from '@/components/information-line';
import { SearchOrganization } from '@/components/kvk-search';
import { FooterNavigation } from '@/components/navigation/footer-navigation';
import { queryClient } from '@/lib/query-client';
import { useOffline } from '@/stores/network-status';

import type { OrganizationFormValues } from '../client';

export const Route = createFileRoute(
    '/(auth)/projects/$projectVersionId/applications/those-involved/managers/',
)({
    component: Page,
    loader: ({ context, params }) => {
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.request.organizationOptions({ electricalProjectVersionId: params.projectVersionId }));
    },
});

type FormValues = OrganizationFormValues & {
    isObjectTenant: boolean;
};

type Organization
    = components['schemas']['ListElectricalProjectOrganizationQueryResultOrganization'];

function Page() {
    return (
        <Section>
            <Section.Content>
                <Form />
            </Section.Content>
            <Section.Footer>
                <FooterNavigation />
            </Section.Footer>
        </Section>
    );
}

function Form() {
    const params = Route.useParams();

    const { data } = useQuery(
        elektraAppQueryOptions.project.request.organizationOptions({ electricalProjectVersionId: params.projectVersionId }),
    );

    const organisations = data?.Organizations?.filter(x => x.IsObjectAdministrator) || [];

    return (
        <>
            <SearchOrganization
                params={params}
                organisationRoles={{
                    isProjectCustomer: false,
                    isObjectAdministrator: true,
                    isObjectOwner: false,
                    isObjectTenant: false,
                }}
            />
            {organisations.length > 0 && (
                <Separator />
            )}
            {organisations.map(organization => <OrganizationInfo key={organization.ElectricalProjectOrganizationId} organization={organization} />)}
        </>
    );
}

function OrganizationInfo({ organization }: { organization: Organization }) {
    const { t } = useTranslation('applications');

    const isUserOffline = useOffline();

    const params = Route.useParams();

    const deleteOrganizationMutation = api.useMutation(
        'delete',
        '/api/v2/electrical/projects/organizations/delete',
        {
            onSuccess: () => {
                toast.success('Organization verwijderd');
                queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.request.organizationOptions({ electricalProjectVersionId: params.projectVersionId }),
                );
            },
        },
    );

    const { Field } = useForm<FormValues, ZodValidator>({
        defaultValues: {
            name: organization?.Name || '',
            email: organization?.Email || '',
            telephoneNumber: organization?.TelephoneNumber || '',
            relationId: 'TODO',
            isObjectTenant: organization?.IsObjectTenant || false,
            contactPersons: [],
            address: {
                street: organization?.Address?.Street || '',
                streetNumber: organization?.Address?.StreetNumber || '',
                postalCode: organization?.Address?.PostalCode || '',
                locality: organization?.Address?.Locality || '',
            },
        }, // /api/v2/electrical/projects/organizations/create
        validatorAdapter: zodValidator(),
    });

    async function handleDelete() {
        await deleteOrganizationMutation.mutateAsync({
            params: {
                query: {
                    ElectricalProjectOrganizationId:
                        organization.ElectricalProjectOrganizationId,
                },
            },
        });
    }

    const { patchField } = usePatcher(
        '/api/v2/electrical/projects/organizations/patch',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.request.organizationOptions({ electricalProjectVersionId: params.projectVersionId }),
                );
            },
        },
        { body: { ElectricalProjectOrganizationId: organization.ElectricalProjectOrganizationId } },
    );

    return (
        <div
            className="flex flex-col gap-4 rounded-lg border border-border p-4"
        >
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold">{organization?.Name}</h2>
                    <p className="text-text-tertiary">Gebouwbeheerder</p>
                </div>
                <Button variant="ghost" onClick={handleDelete} disabled={deleteOrganizationMutation.isPending || isUserOffline}>
                    <X />
                </Button>
            </div>
            <Separator />
            {
                organization?.LegalPersonKind === 'JuridicalPerson'
                    ? (

                            <div className="flex flex-col gap-2">
                                <InformationLine
                                    label="KvK-nummer"
                                    value={organization?.BusinessRegistration?.Number}
                                />
                                {organization?.BusinessRegistration?.BranchNumber && (
                                    <InformationLine
                                        label="Vestigingsnummer"
                                        value={organization?.BusinessRegistration?.BranchNumber}
                                    />
                                )}
                                <InformationLine
                                    label="Straatnaam en huisnummer"
                                    value={
                                        `${organization?.Address?.Street
                                        } ${organization?.Address?.StreetNumber}`
                                    }
                                />
                                <InformationLine
                                    label="Postcode en plaats"
                                    value={
                                        `${organization?.Address?.PostalCode
                                        } ${organization?.Address?.Locality}`
                                    }
                                />
                            </div>
                        )
                    : (
                            <>
                                <Field
                                    name="name"
                                    validators={{
                                        onChange:
                                                    validators.editElectricalProjectOrganizationCommand.name,
                                        onChangeAsync: patchField('name'),
                                        onChangeAsyncDebounceMs: debounceTime,
                                    }}
                                >
                                    {({ handleChange, state, name }) => (
                                        <Label
                                            label={t('those-involved.managers.form.name.label')}
                                        >
                                            <Input
                                                type="text"
                                                name={name}
                                                onChange={e => handleChange(e.target.value)}
                                                value={state.value}
                                                placeholder={t('those-involved.managers.form.name.placeholder')}
                                                disabled={isUserOffline}

                                            />
                                            <FieldInfo state={state} />
                                        </Label>
                                    )}
                                </Field>
                                <div className="grid grid-cols-2 gap-2">
                                    <Field
                                        name="address.street"
                                        validators={{
                                            onChange:
                                                        validators.editElectricalProjectOrganizationCommand.editElectricalProjectOrganizationCommandAddress.street,
                                            onChangeAsync: patchField('address.street'),
                                            onChangeAsyncDebounceMs: debounceTime,
                                        }}
                                    >
                                        {({ handleChange, state, name }) => (
                                            <Label
                                                label={t('those-involved.managers.form.streetname.label')}
                                            >
                                                <Input
                                                    type="text"
                                                    name={name}
                                                    onChange={e => handleChange(e.target.value)}
                                                    value={state.value}
                                                    placeholder={t('those-involved.managers.form.streetname.placeholder')}
                                                    disabled={isUserOffline}
                                                />
                                                <FieldInfo state={state} />
                                            </Label>
                                        )}
                                    </Field>
                                    <Field
                                        name="address.streetNumber"
                                        validators={{
                                            onChange:
                                                        validators.editElectricalProjectOrganizationCommand.editElectricalProjectOrganizationCommandAddress.streetNumber,
                                            onChangeAsync: patchField('address.streetNumber'),
                                            onChangeAsyncDebounceMs: debounceTime,
                                        }}
                                    >
                                        {({ handleChange, state, name }) => (
                                            <Label
                                                label={t('those-involved.managers.form.streetnumber.label')}
                                            >
                                                <Input
                                                    type="text"
                                                    name={name}
                                                    onChange={e => handleChange(e.target.value)}
                                                    value={state.value}
                                                    placeholder={t('those-involved.managers.form.streetnumber.placeholder')}
                                                    disabled={isUserOffline}
                                                />
                                                <FieldInfo state={state} />
                                            </Label>
                                        )}
                                    </Field>
                                    <Field
                                        name="address.postalCode"
                                        validators={{
                                            onChange:
                                                        validators.editElectricalProjectOrganizationCommand.editElectricalProjectOrganizationCommandAddress.postalCode,
                                            onChangeAsync: patchField('address.postalCode'),
                                            onChangeAsyncDebounceMs: debounceTime,
                                        }}
                                    >
                                        {({ handleChange, state, name }) => (
                                            <Label
                                                label={t('those-involved.managers.form.postalcode.label')}
                                            >
                                                <Input
                                                    type="text"
                                                    name={name}
                                                    onChange={e => handleChange(e.target.value)}
                                                    value={state.value}
                                                    placeholder={t('those-involved.managers.form.postalcode.placeholder')}
                                                    disabled={isUserOffline}
                                                />
                                                <FieldInfo state={state} />
                                            </Label>
                                        )}
                                    </Field>
                                    <Field
                                        name="address.locality"
                                        validators={{
                                            onChange:
                                                        validators.editElectricalProjectOrganizationCommand.editElectricalProjectOrganizationCommandAddress.locality,
                                            onChangeAsync: patchField('address.locality'),
                                            onChangeAsyncDebounceMs: debounceTime,
                                        }}
                                    >
                                        {({ handleChange, state, name }) => (
                                            <Label
                                                label={t('those-involved.managers.form.locality.label')}
                                            >
                                                <Input
                                                    type="text"
                                                    name={name}
                                                    onChange={e => handleChange(e.target.value)}
                                                    value={state.value}
                                                    placeholder={t('those-involved.managers.form.locality.placeholder')}
                                                    disabled={isUserOffline}
                                                />
                                                <FieldInfo state={state} />
                                            </Label>
                                        )}
                                    </Field>
                                </div>
                            </>
                        )
            }
            <Field
                name="email"
                validators={{
                    onChange:
                        validators.editElectricalProjectOrganizationCommand.email,
                    onChangeAsync: patchField('email'),
                    onChangeAsyncDebounceMs: debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label
                        label={t(
                            'those-involved.managers.form.general-email.label',
                        )}
                    >
                        <Input
                            type="text"
                            name={name}
                            onChange={e => handleChange(e.target.value)}
                            value={state.value}
                            placeholder={t(
                                'those-involved.managers.form.general-email.placeholder',
                            )}
                            disabled={isUserOffline}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </Field>
            <Field
                name="telephoneNumber"
                validators={{
                    onChange:
                        validators.editElectricalProjectOrganizationCommand.telephoneNumber,
                    onChangeAsync: patchField('telephoneNumber'),
                    onChangeAsyncDebounceMs: debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label
                        label={t(
                            'those-involved.managers.form.general-phone.label',
                        )}
                    >
                        <Input
                            type="text"
                            name={name}
                            onChange={e => handleChange(e.target.value)}
                            value={state.value}
                            placeholder={t(
                                'those-involved.managers.form.general-phone.placeholder',
                            )}
                            disabled={isUserOffline}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </Field>
            {/* <Field name="relationId">
                    {({ handleChange, state, name }) => (
                        <Label
                            label={t(
                                'those-involved.managers.form.relation-id.label'
                            )}
                        >
                            <Input
                                type="text"
                                name={name}
                                onChange={(e) => handleChange(e.target.value)}
                                value={state.value}
                                placeholder={t(
                                    'those-involved.managers.form.relation-id.placeholder'
                                )}
                            />
                        </Label>
                    )}
                </Field> */}
            <div className="flex flex-col">
                <Field
                    name="isObjectTenant"
                    validators={{
                        onChange:
                            validators.editElectricalProjectOrganizationCommand.isObjectTenant,
                        onChangeAsync: patchField('isObjectTenant'),
                        onChangeAsyncDebounceMs: debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <>
                            <CheckboxField
                                name={name}
                                label="Gebouwbeheerder is ook gebouwgebruiker"
                                value={state.value}
                                onChange={handleChange}
                                disabled={isUserOffline}
                            />
                            <FieldInfo state={state} />
                        </>
                    )}
                </Field>
            </div>
            <ContactPersonsList electricalProjectVersionId={params.projectVersionId} organization={organization} disabled={isUserOffline} />
        </div>
    );
}
